/**
 * Unit Categories Configuration
 * Defines the unit breakdown categories for each dealership location
 */

export interface UnitCategory {
  key: string;
  label: string;
  department: 'NEW_VEHICLE' | 'USED_VEHICLE';
}

export interface LocationUnitConfig {
  locationId: number;
  locationName: string;
  newVehicleCategories: UnitCategory[];
  usedVehicleCategories: UnitCategory[];
}

export const LOCATION_UNIT_CONFIGS: Record<number, LocationUnitConfig> = {
  // 1 - Darling's Ford VW Audi
  1: {
    locationId: 1,
    locationName: "Darling's Ford VW Audi",
    newVehicleCategories: [
      { key: 'new_ford', label: 'New Ford Units', department: 'NEW_VEHICLE' },
      { key: 'new_fleet', label: 'New Fleet Units', department: 'NEW_VEHICLE' },
      { key: 'new_vw', label: 'New VW Units', department: 'NEW_VEHICLE' },
      { key: 'new_audi', label: 'New Audi Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used_fva', label: 'Used FVA Units', department: 'USED_VEHICLE' },
      { key: 'used_vc', label: 'Used VC Units', department: 'USED_VEHICLE' },
    ],
  },

  // 2 - Darling's Honda Nissan Volvo
  2: {
    locationId: 2,
    locationName: "Darling's Honda Nissan Volvo",
    newVehicleCategories: [
      { key: 'new_honda', label: 'New Honda Units', department: 'NEW_VEHICLE' },
      { key: 'new_nissan', label: 'New Nissan Units', department: 'NEW_VEHICLE' },
      { key: 'new_volvo', label: 'New Volvo Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 3 - Darling's Auto Mall
  3: {
    locationId: 3,
    locationName: "Darling's Auto Mall",
    newVehicleCategories: [
      { key: 'new_gm', label: 'New GM Units', department: 'NEW_VEHICLE' },
      { key: 'new_stellantis', label: 'New Stellantis Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 4 - Darling's Chrysler Dodge Hyundai
  4: {
    locationId: 4,
    locationName: "Darling's Chrysler Dodge Hyundai",
    newVehicleCategories: [
      { key: 'new_hyundai', label: 'New Hyundai Units', department: 'NEW_VEHICLE' },
      { key: 'new_stellantis', label: 'New Stellantis Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 5 - Darling's Chevrolet
  5: {
    locationId: 5,
    locationName: "Darling's Chevrolet",
    newVehicleCategories: [
      { key: 'new_chevrolet', label: 'New Chevrolet Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 10 - Darling's Brunswick Ford
  10: {
    locationId: 10,
    locationName: "Darling's Brunswick Ford",
    newVehicleCategories: [
      { key: 'new_ford', label: 'New Ford Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 11 - Darling's CDJR Newport
  11: {
    locationId: 11,
    locationName: "Darling's CDJR Newport",
    newVehicleCategories: [
      { key: 'new_stellantis', label: 'New Stellantis Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 12 - Darling's Green Point
  12: {
    locationId: 12,
    locationName: "Darling's Green Point",
    newVehicleCategories: [
      { key: 'new', label: 'New Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 14 - Darling's Kia
  14: {
    locationId: 14,
    locationName: "Darling's Kia",
    newVehicleCategories: [
      { key: 'new_kia', label: 'New Kia Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },

  // 44 - Darling's Undercoating & More
  44: {
    locationId: 44,
    locationName: "Darling's Undercoating & More",
    newVehicleCategories: [
      { key: 'new', label: 'New Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  },
};

/**
 * Get unit configuration for a specific location
 */
export function getLocationUnitConfig(locationId: number): LocationUnitConfig | null {
  return LOCATION_UNIT_CONFIGS[locationId] || null;
}

/**
 * Get default unit config for locations not explicitly configured
 */
export function getDefaultUnitConfig(locationId: number, locationName: string): LocationUnitConfig {
  return {
    locationId,
    locationName,
    newVehicleCategories: [
      { key: 'new', label: 'New Units', department: 'NEW_VEHICLE' },
    ],
    usedVehicleCategories: [
      { key: 'used', label: 'Used Units', department: 'USED_VEHICLE' },
    ],
  };
}
